// src/modules/app-views/_apps/appsindico/units/config.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import UnitsInit from './pages/Init';
import ListProvider from 'components/core/ListProvider';
import DocumentProvider from 'components/core/DocumentProvider';
import UnitsDocument from './pages/Document';
import unitsInitActionButton from './pages/Init/actions/actionButton';
import unitsInitMenuButton from './pages/Init/actions/menuButton';
import unitsDocumentActionButton from './pages/Document/actions/actionButton';
import unitsDocumentMenuButton from './pages/Document/actions/menuButton';
import unitsDocumentDecorateRequest from './pages/Document/decorators/request';
import CardHeaderTitle from 'layouts/Pages/PageHeader/CardHeaderTitle';
import UnitsParametersModal from './pages/Init/modals/UnitsParametersModal';
import seedUnitsParameters from './seed';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'units';

// *******************************************************************************
// PRE CONFIG
// *******************************************************************************
const preConfigChanges = appId => {
  CONFIG.appId = appId;
};

// *******************************************************************************
// MODULE CONFIGURATION
// *******************************************************************************
const CONFIG = {
  version: 2,
  id: MODULE_ID,
  initialPage: 'init',
  initialParams: {},
  initialQuery: {},
  parametersModalComponent: UnitsParametersModal,
  parametersSeed: seedUnitsParameters,
};

// *******************************************************************************
// MODULE PAGES
// *******************************************************************************
const PAGES = {
  init: {
    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'list',
    moduleId: MODULE_ID,
    component: props => (
      <ListProvider {...props}>
        <UnitsInit />
      </ListProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: '/v1/units/pagination',
      params: null,
      query: null,

      // data transformation
      returnAsObject: false,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: null,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: null,
      paramKey: null,
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: {
      pagination: { page: 1, size: 10 },
      sort: [{ field: '_metadata.audit.updatedAt', order: 'desc', index: 1 }],
      search: {},
      textSearch: '',
      filter: {},
      drawer: { folder: 'documents' },
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: {
      actions: ['edit', 'archive', 'remove'],
      hiddenColumns: [],
    },

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: null,

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'default',
      actions: ['filter', 'refresh', unitsInitActionButton(), unitsInitMenuButton()],

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['management', 'units'],
      },
    },
  },

  document: {
    highSecurity: false,

    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'document',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props}>
        <UnitsDocument />
      </DocumentProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: '/v1/units',
      params: null,
      query: null,

      // data transformation
      returnAsObject: true,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: unitsDocumentDecorateRequest,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: '/:_id',
      paramKey: '_id',
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: null,

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: null,

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: {
      initialTab: 'record',
      initialValues: {
        status: 'vacant',
        habitationType: 'residential',
      },
      actions: ['editRefresh', unitsDocumentActionButton(), unitsDocumentMenuButton(), 'return'],
      viewMode: 'auto',
    },

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'card',

      avatar: {
        visible: false,
      },

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['management', 'units'],
      },

      card: {
        title: props => <CardHeaderTitle {...props} placeholder="new_unit" />,
        subtitle: () => null,
        column1: () => null,
        column2: () => null,
      },
    },
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default { CONFIG, PAGES, preConfigChanges };
